import { IPermissionProps } from '@/types/shared'
import { useParams } from 'react-router-dom'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { getJobByID, getJobCategories, updateJob } from '../../services'
import { GridInformationWithHookForm } from 'vnetwork-ui'
import SubHeaders from '@/components/Headers/SubHeaders'
import { paths } from '@/utils/path'
import { useTranslation } from 'react-i18next'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import { Job } from '../../types'
import {
    JOB_COUNTRY,
    JOB_LEVEL,
    JOB_STATUS,
    JOB_TYPE,
    LOCALE_OPTIONS,
} from '../../constants'
import PeopleIcons from '@/icons/PeopleIcons'
import { createJobSchema } from '../Create/yup'
import { getAllDepartments } from '@/modules/Employee/services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

const Update: React.FC<IPermissionProps> = () => {
    const { jobID } = useParams<{ jobID: string }>()
    const { t } = useTranslation()
    const { refetchCurrent } = useDataGridRefetch({
        reactQueryKey: 'jobs',
    })
    const {
        data: job,
        refetch,
        renderState,
    } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.JOB_DETAIL, jobID],
            queryFn: async () => {
                const result = await getJobByID(Number(jobID))
                return result
            },
        },
        {
            errorDisplayMode: 'page',
        }
    )
    const { data } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.JOB_CATEGORIES_LIST],
        queryFn: async () => await getJobCategories(),
    })
    const { data: departments } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.DEPARTMENTS],
        queryFn: getAllDepartments,
    })
    const handleEdit = async (values: Job) => {
        console.log('values', values)
        await updateJob({
            id: Number(jobID),
            job: values,
        })

        refetchCurrent()
    }

    const { mutateAsync: updatejobMutation } = useMutationWithHandler(
        {
            mutationFn: handleEdit,
        },
        {
            reactQueryKeys: QUERY_KEYS.JOBS,
            successMessage: t('updated-success'),
            onSuccess: () => {
                refetch()
            },
        }
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent
    return (
        <>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: '/jobs',
                        title: t('job-list'),
                    },
                    {
                        path: `${paths.jobList}/${job?.result?.job_id}`,
                        title: t('job-information'),
                    },
                ]}
            />

            <GridInformationWithHookForm
                defaultFormValues={{
                    title: job?.result?.title || '',
                    description: job?.result?.description || '',
                    slug: job?.result?.slug || '',
                    locale: job?.result?.locale || '',
                    location: job?.result?.location || '',
                    level: job?.result?.level || '',
                    type: job?.result?.type || '',
                    categories: job?.result?.categories || [],
                    summary: job?.result?.summary || '',
                    country: job?.result?.country || '',
                    salary: job?.result?.salary || '',
                    status: job?.result?.status || '',
                    responsible: job?.result?.responsible || '',
                    qualification: job?.result?.qualification || '',
                    benefit: job?.result?.benefit || '',
                    department: job?.result?.department || '',
                    required_skills: job?.result?.required_skills || '',
                }}
                onEdit={updatejobMutation}
                schema={createJobSchema as any}
                data={[
                    {
                        label: t('job-title'),
                        value: job?.result?.title,
                        formItemProps: {
                            name: 'title',
                            type: 'textfield',
                        },
                    },
                    {
                        label: t('job-description'),
                        value: job?.result?.description,
                        formItemProps: {
                            name: 'description',
                            type: 'textfield',
                        },
                    },
                    {
                        label: 'Slug',
                        value: job?.result?.slug,
                        formItemProps: {
                            name: 'slug',
                            type: 'textfield',
                            disabled: true,
                        },
                    },
                    {
                        label: t('job-locale'),
                        value: job?.result?.locale,
                        formItemProps: {
                            name: 'locale',
                            type: 'select',
                            options: LOCALE_OPTIONS,
                        },
                    },
                    {
                        label: t('job-categories'),
                        value: job?.result?.categories
                            ?.map(
                                (categoryId) =>
                                    data?.results.find(
                                        (item) => item.id === categoryId
                                    )?.title
                            )
                            ?.filter(Boolean)
                            .join(', '),
                        formItemProps: {
                            name: 'categories',
                            type: 'select',
                            multiple: true,
                            options:
                                data?.results.map((item) => ({
                                    label: item?.title,
                                    value: item?.id,
                                })) || [],
                        },
                    },
                    {
                        label: t('job-location'),
                        value: job?.result?.location,
                        formItemProps: {
                            name: 'location',
                            type: 'textarea',
                        },
                    },
                    {
                        label: t('job-level'),
                        value: JOB_LEVEL.find(
                            (item) => item.value === job?.result?.level
                        )?.label,
                        formItemProps: {
                            name: 'level',
                            type: 'select',
                            options: JOB_LEVEL,
                        },
                    },
                    {
                        label: t('job-type'),
                        value: JOB_TYPE.find(
                            (item) => item.value === job?.result?.type
                        )?.label,
                        formItemProps: {
                            name: 'type',
                            type: 'select',
                            options: JOB_TYPE,
                        },
                    },
                    {
                        label: t('job-summary'),
                        value: job?.result?.summary,
                        formItemProps: {
                            name: 'summary',
                            type: 'textarea',
                        },
                    },
                    {
                        label: t('job-country'),
                        value: JOB_COUNTRY.find(
                            (item) => item.value == job?.result?.country
                        )?.label,
                        formItemProps: {
                            name: 'country',
                            type: 'select',
                            options: JOB_COUNTRY,
                        },
                    },
                    {
                        label: t('job-salary'),
                        value: job?.result?.salary,
                        formItemProps: {
                            name: 'salary',
                            type: 'textfield',
                        },
                    },
                    {
                        label: t('job-status'),
                        value: JOB_STATUS(t).find(
                            (item) => item.value == job?.result?.status
                        )?.label,
                        formItemProps: {
                            name: 'status',
                            type: 'select',
                            options: JOB_STATUS(t),
                        },
                    },
                    {
                        label: t('job-responsible'),
                        value: job?.result?.responsible,
                        formItemProps: {
                            name: 'responsible',
                            type: 'textarea',
                        },
                        className: 'col-span-4',
                    },
                    {
                        label: t('job-qualification'),
                        value: job?.result?.qualification,
                        formItemProps: {
                            name: 'qualification',
                            type: 'textarea',
                        },
                        className: 'col-span-4',
                    },
                    {
                        label: t('job-benefit'),
                        value: job?.result?.benefit,
                        formItemProps: {
                            name: 'benefit',
                            type: 'textarea',
                        },
                        className: 'col-span-4',
                    },
                    {
                        label: t('department'),
                        value: job?.result?.department,
                        formItemProps: {
                            name: 'department',
                            type: 'select',
                            options:
                                departments?.results.map((item) => ({
                                    label: item?.department_name,
                                    value: item?.department_name,
                                })) || [],
                        },
                    },
                    {
                        label: t('job-required-skills'),
                        value: job?.result?.required_skills,
                        formItemProps: {
                            name: 'required_skills',
                            type: 'textarea',
                        },
                        className: 'col-span-4',
                    },
                ]}
                head={{
                    title: t('job-information'),
                    icon: <PeopleIcons />,
                }}
            />
        </>
    )
}

export default Update
